import type { HttpError } from '@refinedev/core';
import { notification } from 'antd';

import type { ServerError } from '@/config/data-provider/types';

const extractMessages = (rawDetail: unknown): string[] => {
  if (Array.isArray(rawDetail)) {
    return rawDetail.map((m: ServerError) => m.message);
  }
  if (typeof rawDetail === 'string') {
    return [rawDetail];
  }
  return [];
};

const statusMessages: Record<number, string> = {
  403: 'Forbidden',
  500: 'The server is unavailable right now'
};

type AsyncFn<TArgs extends unknown[], TReturn> = (
  ...args: TArgs
) => Promise<TReturn>;

/**
 * Higher-order function that wraps an async function with global error handling.
 * - Executes the wrapped function.
 * - If an error is thrown, it will trigger the notification handler (unless disabled).
 * - Re-throws the error so local `try/catch` or React Query can still handle it.
 */
export function withErrorNotification<TArgs extends unknown[], TReturn>(
  fn: AsyncFn<TArgs, TReturn>,
  notify = true
): AsyncFn<TArgs, TReturn> {
  return async (...args: TArgs): Promise<TReturn> => {
    try {
      return await fn(...args);
    } catch (error) {
      if (notify) {
        handleError(error as HttpError);
        (error as HttpError).message =
          `${(error as HttpError).message}_global-notified`;
      }
      throw error;
    }
  };
}

/**
 * Centralized error handler:
 * - Extracts error details from `HttpError` (status + backend-provided messages).
 * - Displays backend-provided messages when available.
 * - Falls back to predefined messages by status code (from `statusMessages`).
 * - Ensures unique error messages are shown (avoids spamming duplicates).
 */
export function handleError(error: HttpError) {
  const status = error.response?.status;
  const rawDetail =
    error.response?.data?.detail ?? error.response?.data?.message;

  const messages = extractMessages(rawDetail);

  if (messages.length) {
    [...new Set(messages)].forEach(msg =>
      notification.error({ message: msg, pauseOnHover: true })
    );
    return;
  }

  const fallback =
    statusMessages[status ?? 0] || 'Failed to process your request';

  notification.error({ message: fallback });
}
